import * as THREE from 'three';

import { scaffoldLook } from '../buildings/BuildingShapes';
import type { RoadCell } from '../../network/Roads';
import { Axial, axialKey, axialToWorld } from './HexCoordinates';
import { elevationToY } from './HexMetrics';
import { HexCellData, isWater, waterLevel } from './HexSurface';

/**
 * Budowa dróg i mostów: to, co widać, zanim droga zacznie kogokolwiek nieść.
 *
 * - **Most w budowie** dostaje rusztowanie (ten sam model, co place budowy),
 *   pasek postępu i słup kurzu.
 * - **Droga w budowie** jest wykopem (rysuje go streamer, innym materiałem)
 *   i kurzy się — ale **nie każde pole**: co `DUST_EVERY` pól, wybierane
 *   z położenia, żeby długi trakt nie zasypał ekranu, a ten sam odcinek kurzył
 *   się tak samo u wszystkich.
 *
 * Czas bierze się z tego samego zegara, którym jedzie `roadPending`: moment
 * gotowości na zegarze klienta, zapisany przy przyjęciu drogi.
 */

/** Jedna droga w pamięci gry — kształt mapy `roads` z `Game`. */
export interface HeldRoad {
  cell: RoadCell;
  readyAt: number;
}

/** Co któreś pole drogi w budowie się kurzy. */
const DUST_EVERY = 4;

/** Wysokość rusztowania nad mostem, w jednostkach świata. */
const SCAFFOLD_HEIGHT = 0.3;

/** Na jakiej wysokości wisi pasek postępu nad mostem. */
const BAR_LIFT = 0.4;

export interface WorkSite {
  id: string;
  /** Skąd idzie kurz. */
  base: THREE.Vector3;
  /** Gdzie wisi pasek — tylko przy mostach. */
  position: THREE.Vector3;
  progress: number;
  /** Czy to most — tylko mosty mają pasek. */
  bridge: boolean;
}

export class RoadWorks {
  readonly object = new THREE.Group();
  private readonly scaffolds = new Map<number, THREE.Object3D>();

  constructor(
    private readonly world: Map<number, HexCellData>,
    /** Ile trwa budowa mostu, z katalogu — mianownik paska. */
    private readonly bridgeSeconds: number
  ) {
    this.object.name = 'road-works';
  }

  /**
   * Co się teraz buduje: mosty (każdy) i co któreś pole drogi. Po drodze
   * stawia i zdejmuje rusztowania mostów.
   */
  update(roads: Map<number, HeldRoad>, now: number): WorkSite[] {
    const sites: WorkSite[] = [];
    const building = new Set<number>();

    for (const [key, road] of roads) {
      if (road.readyAt <= now) continue;
      const data = this.world.get(key);
      if (!data) continue;

      const { q, r } = road.cell;
      const { x, z } = axialToWorld(q, r);
      const onWater = isWater(data);
      const y = elevationToY(onWater ? (waterLevel(data) ?? 0) : data.elevation);
      const left = (road.readyAt - now) / 1000;

      if (onWater) {
        building.add(key);
        this.raise(key, x, y, z);
        sites.push({
          id: `bridge:${q},${r}`,
          base: new THREE.Vector3(x, y, z),
          position: new THREE.Vector3(x, y + BAR_LIFT, z),
          progress: Math.min(1, Math.max(0, 1 - left / Math.max(1, this.bridgeSeconds))),
          bridge: true,
        });
        continue;
      }

      if (dusty(road.cell)) {
        sites.push({
          id: `road:${q},${r}`,
          base: new THREE.Vector3(x, y, z),
          position: new THREE.Vector3(x, y, z),
          progress: 0,
          bridge: false,
        });
      }
    }

    for (const [key, scaffold] of this.scaffolds) {
      if (building.has(key)) continue;
      this.object.remove(scaffold);
      this.scaffolds.delete(key);
    }

    return sites;
  }

  dispose(): void {
    this.scaffolds.clear();
    this.object.removeFromParent();
  }

  private raise(key: number, x: number, y: number, z: number): void {
    if (this.scaffolds.has(key)) return;
    const look = scaffoldLook();
    if (!look) return;

    const scaffold = new THREE.Group();
    for (const part of look.parts) {
      const mesh = new THREE.Mesh(
        part.geometry,
        part.material ?? new THREE.MeshLambertMaterial({ color: 0x8a6a45 })
      );
      mesh.castShadow = true;
      scaffold.add(mesh);
    }
    scaffold.scale.setScalar(SCAFFOLD_HEIGHT / Math.max(look.height, 1e-6));
    scaffold.position.set(x, y, z);
    this.object.add(scaffold);
    this.scaffolds.set(key, scaffold);
  }
}

/** Czy to pole drogi się kurzy — z położenia, więc tak samo u wszystkich. */
function dusty(cell: Axial): boolean {
  const hash = Math.abs(axialKey(cell.q, cell.r) * 2654435761) % DUST_EVERY;
  return hash === 0;
}
